import { useGetRateWld } from '@/hooks/useGetRateWld';
import { Language } from '@/i18n/language';
import { useAuth } from '@/store/auth';
import { formatAddress } from '@/utils/format-address';
import { useMemo } from 'react';
import { useShallow } from 'zustand/shallow';
import { formatDisplay } from '@/utils/format-display';
import BigNumber from 'bignumber.js';
import { BarChartIcon, TrendingUpIcon } from '@/icons';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useBalance } from '@/hooks/useBalance';
import { usePortfolios } from '@/hooks/usePortfolios';
import { Spinner } from '../spinner';

export function HeaderMarket() {
    const [address, profile_picture_url, username, loading] = useAuth(
        useShallow((state) => [
            state.address,
            state.profile_picture_url,
            state.username,
            state.loading,
        ])
    );

    const { quoteRate } = useGetRateWld();
    const { data } = useBalance();
    const { data: portfolios } = usePortfolios();
    const { t } = useTranslation();

    const dataPortfolios = useMemo(() => {
        if (!portfolios) {
            return {
                totalPnl: undefined,
                tradingVolume: undefined,
            };
        }
        return {
            ...portfolios,
            totalPnl: new BigNumber(portfolios?.totalPnl ?? 0).toNumber(),

            tradingVolume: formatDisplay(
                new BigNumber(portfolios?.tradingVolume ?? 0).toNumber(),
                { decimalToShow: 2 }
            ),
        };
    }, [portfolios]);

    const balance = useMemo(() => {
        if (!address || data === undefined) {
            return {
                amount: undefined,
                balance: undefined,
            };
        }
        const amount = new BigNumber(data.toString())
            .dividedBy(1e18)
            .toNumber();
        return {
            amount: formatDisplay(amount, { decimalToShow: 2 }),
            balance: formatDisplay(amount * quoteRate, { decimalToShow: 2 }),
        };
    }, [address, data, quoteRate]);

    return (
        <header className="bg-white px-5 pt-6 pb-4">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                    {profile_picture_url ? (
                        <img
                            src={profile_picture_url}
                            className="w-8 h-8 rounded-full object-cover"
                        />
                    ) : (
                        <div className="uppercase size-8 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold shadow-soft text-xs">
                            {username?.slice(0, 2)}
                        </div>
                    )}
                    <p className="text-sm font-bold text-slate-900">
                        {!loading
                            ? username || formatAddress(address, 8)
                            : '--'}
                    </p>
                </div>
                <Language />
            </div>
            <div className="mb-6">
                <div className="flex items-baseline gap-2">
                    {typeof balance.amount == 'undefined' ? (
                        <Spinner className="w-8 h-8" />
                    ) : (
                        <h1 className="text-[32px] font-bold tracking-tight text-slate-900 tabular-nums">
                            {`${balance.amount} WLD`}
                        </h1>
                    )}
                </div>
                {typeof balance.amount == 'undefined' ? (
                    <Spinner className="w-4 h-4" />
                ) : (
                    <p className="text-sm font-medium text-slate-400 tabular-nums">
                        ≈ ${`${balance.balance}`}
                    </p>
                )}
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div className="bg-surface-light p-4 rounded-2xl">
                    <div className="flex items-center gap-1.5 mb-2 text-slate-500">
                        <BarChartIcon className="w-5 h-5" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">
                            {t('Total Volume')}
                        </span>
                    </div>
                    {typeof dataPortfolios?.tradingVolume == 'undefined' ? (
                        <Spinner className="w-6 h-6 text-slate-900" />
                    ) : (
                        <p className="text-lg font-bold text-slate-900 tabular-nums">
                            ${`${dataPortfolios?.tradingVolume}`}
                        </p>
                    )}
                </div>
                <div className="bg-surface-light p-4 rounded-2xl">
                    <div className="flex items-center gap-1.5 mb-2 text-slate-500">
                        <TrendingUpIcon className="w-5 h-5" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">
                            {t('Net Profit')}
                        </span>
                    </div>

                    {typeof dataPortfolios?.totalPnl === 'number' ? (
                        <p
                            className={clsx(
                                'text-lg font-bold text-ios-blue tabular-nums',
                                {
                                    '!text-[#38F24A]':
                                        Number(dataPortfolios?.totalPnl) > 0,
                                    '!text-[#ff695f]':
                                        Number(dataPortfolios?.totalPnl) < 0,
                                }
                            )}
                        >
                            {Number(dataPortfolios?.totalPnl) > 0
                                ? '+'
                                : Number(dataPortfolios?.totalPnl) < 0
                                ? '-'
                                : ''}
                            $
                            {formatDisplay(
                                Math.abs(Number(dataPortfolios?.totalPnl || 0)),
                                { decimalToShow: 2 }
                            )}
                        </p>
                    ) : (
                        <Spinner className="w-6 h-6 text-slate-900" />
                    )}
                </div>
            </div>
        </header>
    );
}
